import { Component, inject, input, effect, signal, computed } from '@angular/core';
import { Process } from '../../../models/engineering/process';
import { Step } from '../../../models/engineering/step';
import { LocaleHelperService } from '../../../services/localehelperservice.service';
import { IngredientUnit } from '../../../models/engineering/enum/IngredientUnit.enum';
import { ItemMaster } from '../../../models/item-master';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { Parameter } from '../../../models/engineering/parameter';
import { ParameterProxyService } from '../../../proxies/item/parameter.proxy.service';
import { ResponseTranslation } from '../../../models/Translations';

type RowType = 'process' | 'step' | 'item-detail';

interface TableRow {
  type: RowType;
  name: string;
  itemCode?: string;
  itemDisplayName?: ResponseTranslation;
  uom?: string;
  qty?: number;
  rate?: number;
  parameters: Map<string, { values: string[]; isRequired: boolean }>;
  backgroundColor?: string;
  processId?: number;
  stepOrder?: number;
}

@Component({
  selector: 'app-process-details-table',
  templateUrl: './process-details-table.component.html',
  styleUrl: './process-details-table.component.scss',
})
export class ProcessDetailsTableComponent {
  private readonly localeHelper = inject(LocaleHelperService);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);
  private readonly parameterProxyService = inject(ParameterProxyService);

  processes = input<Process[]>([]);
  itemMastersMap = signal<Map<string, ItemMaster>>(new Map());
  parametersMap = signal<Map<string, Parameter>>(new Map());

  // Computed signal para generar la lista plana de filas
  flatRows = computed(() => this.buildFlatRows());

  // Computed signal para obtener todas las columnas de parámetros
  allParameterColumns = computed(() => this.getAllParameterColumns());

  constructor() {
    effect(() => {
      const procs = this.processes();
      if (procs && procs.length > 0) {
        this.loadItemMasters(procs);
        this.loadParameters(procs);
      }
    });
  }

  getProcessName(process: Process): string {
    return this.localeHelper.getLocalizedValue(process.name);
  }

  getStepName(step: Step): string {
    return this.localeHelper.getLocalizedValue(step.name);
  }

  getParameterName(parameterRef: string): string {
    const param = this.parametersMap().get(parameterRef);
    if (param && param.name) {
      return this.localeHelper.getLocalizedValue(param.name);
    }
    if (param && param.friendlyName) {
      return this.localeHelper.getLocalizedValue(param.friendlyName);
    }
    return parameterRef;
  }

  getParameterValues(values: string[]): string {
    return values.join(', ');
  }

  private loadItemMasters(processes: Process[]): void {
    const allItemCodes = new Set<string>();

    processes.forEach((process) => {
      process.itemDetails?.forEach((item) => allItemCodes.add(item.itemCode));

      process.stepIdsWithOrder?.forEach((stepOrder) => {
        if (stepOrder.step) {
          stepOrder.step.itemDetails?.forEach((item) => allItemCodes.add(item.itemCode));
        }
      });
    });

    if (allItemCodes.size > 0) {
      const itemCodesArray = Array.from(allItemCodes);
      this.itemMasterProxyService.getItemCodesBatch({ itemCodes: itemCodesArray }).subscribe({
        next: (response) => {
          if (response.data) {
            const map = new Map<string, ItemMaster>();
            response.data.forEach((item) => {
              map.set(item.itemCode, item);
            });
            this.itemMastersMap.set(map);
          }
        },
      });
    }
  }

  private loadParameters(processes: Process[]): void {
    const allParameterRefs = new Set<string>();

    processes.forEach((process) => {
      process.additionalParameters?.forEach((param) => allParameterRefs.add(param.parameterRef));

      process.stepIdsWithOrder?.forEach((stepOrder) => {
        if (stepOrder.step) {
          stepOrder.step.additionalParameters?.forEach((param) => allParameterRefs.add(param.parameterRef));
        }
      });
    });

    if (allParameterRefs.size > 0) {
      this.parameterProxyService.getParameters().subscribe({
        next: (response) => {
          if (response.data) {
            const map = new Map<string, Parameter>();
            response.data.forEach((param) => {
              if (param.id && allParameterRefs.has(param.id)) {
                map.set(param.id, param);
              }
            });
            this.parametersMap.set(map);
          }
        },
      });
    }
  }

  getItemMaster(itemCode: string): ItemMaster | undefined {
    return this.itemMastersMap().get(itemCode);
  }

  getItemDescription(itemCode: string): string {
    const item = this.getItemMaster(itemCode);
    if (item && item.itemDescription) {
      return this.localeHelper.getLocalizedValue(item.itemDescription);
    }
    return '';
  }

  getIngredientUnitName(unit: IngredientUnit | null | undefined): string {
    if (unit === null || unit === undefined) {
      return 'N/A';
    }

    switch (unit) {
      case IngredientUnit.Percent:
        return 'Percent';
      case IngredientUnit.GramsPerLiter:
        return 'Grams per Liter';
      default:
        return String(unit);
    }
  }

  getIngredientUnitAbrev(unit: IngredientUnit | null | undefined): string {
    if (unit === null || unit === undefined) {
      return 'N/A';
    }

    switch (unit) {
      case IngredientUnit.Percent:
        return '%';
      case IngredientUnit.GramsPerLiter:
        return 'g/L';
      default:
        return String(unit);
    }
  }

  private buildFlatRows(): TableRow[] {
    const rows: TableRow[] = [];
    const processes = this.processes();

    processes.forEach((process) => {
      // Agregar fila del proceso
      const processParams = new Map<string, { values: string[]; isRequired: boolean }>();
      process.additionalParameters?.forEach((param) => {
        const paramName = this.getParameterName(param.parameterRef);
        processParams.set(paramName, { values: param.values, isRequired: param.isRequired });
      });

      rows.push({
        type: 'process',
        name: this.getProcessName(process),
        parameters: processParams,
        processId: process.processId,
      });

      // Agregar item details del proceso
      process.itemDetails?.forEach((item) => {
        const itemMaster = this.getItemMaster(item.itemCode);
        rows.push({
          type: 'item-detail',
          name: '',
          itemCode: item.itemCode,
          itemDisplayName: itemMaster?.itemDisplayName,
          uom: this.getIngredientUnitAbrev(item.ingredientUnit),
          qty: item.quantity,
          parameters: new Map(),
          backgroundColor: '#f0f9f0',
        });
      });

      // Agregar steps y sus item details
      process.stepIdsWithOrder?.forEach((stepOrder) => {
        if (stepOrder.step) {
          const step = stepOrder.step;
          const stepParams = new Map<string, { values: string[]; isRequired: boolean }>();

          step.additionalParameters?.forEach((param) => {
            const paramName = this.getParameterName(param.parameterRef);
            stepParams.set(paramName, { values: param.values, isRequired: param.isRequired });
          });

          rows.push({
            type: 'step',
            name: this.getStepName(step),
            parameters: stepParams,
            stepOrder: stepOrder.order,
            backgroundColor: '#f0f9f0',
          });

          // Agregar item details del step
          step.itemDetails?.forEach((item) => {
            const itemMaster = this.getItemMaster(item.itemCode);
            rows.push({
              type: 'item-detail',
              name: '',
              itemCode: item.itemCode,
              itemDisplayName: itemMaster?.itemDisplayName,
              uom: this.getIngredientUnitAbrev(item.ingredientUnit),
              qty: item.quantity,
              parameters: new Map(),
              backgroundColor: '#f0f9f0',
            });
          });
        }
      });
    });

    return rows;
  }

  private getAllParameterColumns(): string[] {
    const paramNamesSet = new Set<string>();
    const processes = this.processes();

    processes.forEach((process) => {
      // Parámetros del proceso
      process.additionalParameters?.forEach((param) => {
        const paramName = this.getParameterName(param.parameterRef);
        paramNamesSet.add(paramName);
      });

      // Parámetros de los steps
      process.stepIdsWithOrder?.forEach((stepOrder) => {
        if (stepOrder.step) {
          stepOrder.step.additionalParameters?.forEach((param) => {
            const paramName = this.getParameterName(param.parameterRef);
            paramNamesSet.add(paramName);
          });
        }
      });
    });

    return Array.from(paramNamesSet);
  }

  getRowParameterValue(row: TableRow, paramName: string): { values: string[]; isRequired: boolean } | null {
    return row.parameters.get(paramName) || null;
  }
}
